import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import resetStyle from '../../shared/components/html-content/reset.style';
import { ludsBaseFont } from '../../shared/styles/typography';
import '../../design-system/modal/modal';
import '../../design-system/button/button';
import { i18n } from '../../i18n/i18n';
import '../../shared/components/social-links/social-links';
import { LuaShareCertificateClient } from './share-certificate-client';
import { TwitterType } from './share-certificate.model';
import { LuaModalElement } from '../../shared/components/lua-modal/lua-modal';

@customElement('lua-share-certificate')
export class LuaShareCertificate extends LuaModalElement {
  /**
   * Enables or disables the Twitter sharing option.
   * If `true`, the Twitter share button is shown.
   */
  @property({ type: Boolean, reflect: true })
  twitterEnabled = false;

  /**
   * Enables or disables the LinkedIn sharing option.
   * If `true`, the LinkedIn share button is shown.
   */
  @property({ type: Boolean, reflect: true })
  linkedinEnabled = false;

  /**
   * The enrollment ID associated with the certificate.
   * Used for sharing on LinkedIn.
   */
  @property({ type: Number, reflect: true })
  enrollmentId: number = null;

  /**
   * Data required for sharing on Twitter, including hashtags, related accounts, and course name.
   */
  @property({ attribute: false })
  twitterData: TwitterType = {
    twitter_hash_tags: '',
    twitter_related_accounts: '',
    course_name: '',
  };

  static override styles = [resetStyle, ludsBaseFont];

  private shareCertificateClient: LuaShareCertificateClient = new LuaShareCertificateClient();

  private closeShareCertificateModal() {
    this.dispatchEvent(
      new CustomEvent('close-certificate', {
        detail: false,
        bubbles: true,
        composed: true,
      })
    );
  }

  private shareLinkedIn(e: Event): void {
    e.preventDefault();
    this.shareCertificateClient.shareOnLinkedIn(this.enrollmentId);
  }

  private shareTwitter(e: Event): void {
    e.preventDefault();
    const popupWinAttrs =
      'status=no, width=' +
      640 +
      ', height=' +
      480 +
      ', resizable=yes, toolbar=no, menubar=no, scrollbars=no, location=no, directories=no';
    const customHashes = this.twitterData.twitter_hash_tags;
    const relatedAccounts = this.twitterData.twitter_related_accounts;
    const text = i18n('lua.share-certificate.predefined_twitter_text', { course_name: this.twitterData.course_name });
    const url = encodeURI(
      'https://twitter.com/intent/tweet?hashtags=' + customHashes + '&related=' + relatedAccounts + '&text=' + text
    );
    window.open(url, 'popupwindow', popupWinAttrs).focus();
  }

  override render() {
    return html`
      <luds-modal
        .open=${this.open}
        title="${i18n('lua.share-certificate.title')}"
        height="sm"
        animation=""
        width="sm"
        id="share-certificate-modal"
        @luds-dialog-close="${this.closeShareCertificateModal}">
        <lua-social-links
          @share-linkedin=${this.shareLinkedIn}
          @share-twitter=${this.shareTwitter}
          .linkedinEnabled=${this.linkedinEnabled}
          .twitterEnabled=${this.twitterEnabled}></lua-social-links>
      </luds-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lua-share-certificate': LuaShareCertificate;
  }
}
